import React, { useEffect } from "react";
import { getQueryParam } from "@/src/services/urlParamService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createResource, fetchCollection, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { CheckoutOrderProvider, useCheckoutOrder } from "@/src/provider/CheckoutOrder/CheckoutOrderProvider";
import _head from "lodash/head";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { Elements } from "@stripe/react-stripe-js";
import { Helmet } from "react-helmet-async";
import Checkout from "@/src/containers/HigherOrderContainers/Checkout";
import { loadStripe } from "@stripe/stripe-js";
import { STRIPE_PUBLIC_KEY } from "@/src/config/_enviroment";
import { useLocation, useNavigate } from "react-router";
import { useTheme } from "@mui/material/styles";
import { Theme } from "@mui/material";
import { PaymentTransaction } from "@/src/types/Entities/PaymentTransaction";
import { CheckoutOrder } from "@/src/types/Entities/CheckoutOrder";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY as string);

export default function StripeCheckout() {
  const location = useLocation();
  const orderId = getQueryParam(location.search, "orderId");

  const navigate = useNavigate();

  if (!orderId) {
    navigate("/servicewelt");
  }

  return (
    <>
      {orderId && (
        <CheckoutOrderProvider orderId={orderId}>
          <StripeCheckoutOrderDataProvider orderIdentifier={orderId} />
        </CheckoutOrderProvider>
      )}
    </>
  );
}

function StripeCheckoutOrderDataProvider({ orderIdentifier }: { orderIdentifier: string }) {
  const { checkoutOrder, isLoading: isLoadingCheckoutOrder, create } = useCheckoutOrder();

  const paymentTransactionApiUri = "/payment_transactions";
  const paymentTransactionFilter = {
    "order.orderIdentifier": orderIdentifier,
  };
  const { data: existingTransactions, isLoading: isLoadingTransaction } = useQuery({
    queryKey: queryKeys.collection(paymentTransactionApiUri, paymentTransactionFilter),
    queryFn: () => fetchCollection(paymentTransactionApiUri, paymentTransactionFilter),
    enabled: !!(checkoutOrder && !isLoadingCheckoutOrder),
  });
  const currentTransaction = existingTransactions?.length > 0 ? existingTransactions[0] : null;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isLoadingCheckoutOrder && !checkoutOrder) {
      create({ orderIdentifier: orderIdentifier }).then(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.collection("/checkout_orders", { orderIdentifier: orderIdentifier }),
        });
      });
    }
  }, [isLoadingCheckoutOrder]);

  if (isLoadingCheckoutOrder || isLoadingTransaction || !checkoutOrder) {
    return (
      <LayoutProtectedRoutes sidebar={false}>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  return <StripeCheckoutInner orderIdentifier={orderIdentifier} currentTransaction={currentTransaction} checkoutOrder={checkoutOrder} />;
}

function StripeCheckoutInner({
  orderIdentifier,
  currentTransaction,
  checkoutOrder,
}: {
  orderIdentifier: string;
  currentTransaction: PaymentTransaction;
  checkoutOrder: CheckoutOrder;
}) {
  const theme = useTheme<Theme>();
  const paymentTransactionApiUri = "/payment_transactions";
  const paymentTransactionFilter = {
    "order.orderIdentifier": orderIdentifier,
  };
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const queryClient = useQueryClient();

  const createTransaction = async () => {
    const orderable = _head(checkoutOrder.product.checkoutItem.orderables);
    if (!orderable) {
      throw new Error("No orderable found");
    }
    const data = {
      order: "/checkout_orders/" + checkoutOrder.id,
      options: {
        order: {
          orderables: ["/orderables/" + orderable.id],
        },
      },
    };

    await createMutation.mutateAsync(
      { uri: paymentTransactionApiUri, data: data },
      {
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: queryKeys.collection(paymentTransactionApiUri, paymentTransactionFilter),
          }),
      },
    );
  };

  useEffect(() => {
    if (!currentTransaction) {
      createTransaction();
    }
  }, []);

  if (!stripePromise || !currentTransaction) {
    return (
      <LayoutProtectedRoutes sidebar={false}>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: currentTransaction.token,
        appearance: { variables: { colorPrimary: theme.palette.primary.main } },
      }}
    >
      <LayoutProtectedRoutes sidebar={false}>
        <Helmet>
          <title>Checkout | Legalbird</title>
        </Helmet>
        <Checkout transaction={currentTransaction} />
      </LayoutProtectedRoutes>
    </Elements>
  );
}
